import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Calendar as CalendarIcon,
  List,
  Funnel,
  Download,
  Upload,
  Search,
} from 'lucide-react';
import { campaignsControllerCancelCampaign } from '@/generated/korza-api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import {
  currencyForDisplay,
  useStoreCurrency,
} from '@/hooks/use-store-currency';
import {
  CAMPAIGNS_PAGE_SIZE,
  type CampaignsFilters,
  useCampaigns,
  useInfiniteCampaigns,
} from '@/hooks/use-campaigns';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import {
  CampaignsMobileHeaderMenu,
  CampaignsMobileList,
  useMobileCampaigns,
} from '@/components/campaigns/campaigns-mobile-list';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  type CampaignStatusFilter,
  parseStatusFilter,
  statusFilterToApi,
} from '@/lib/campaign-list';
import { CampaignStatusFilter as CampaignStatusFilterControl } from '@/components/campaigns/campaign-status-filter';
import { CancelCampaignDialog } from '@/components/campaigns/cancel-campaign-dialog';
import { useCancelCampaign } from '@/hooks/use-cancel-campaign';
import { mobileLayer } from '@/lib/mobile-layer';
import { useNavigate, useParams } from 'react-router';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn, getLuxonLocale } from '@/lib/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CampaignsTable,
  CampaignsTableColumnVisibilityMenu,
} from '@/components/campaigns-table';
import { CampaignsBulkActionsPopover } from '@/components/campaigns-bulk-actions-popover';
import {
  canDeleteCampaigns,
  useCampaignsTable,
} from '@/hooks/use-campaigns-table';
import { Calendar, CalendarNavigation } from '@/components/calendar';
import { useLocalSettings } from '@/hooks/use-local-settings';
import { DateRangeFilter } from '@/components/date-range-filter';
import { exportCampaignsCsv } from '@/lib/campaigns-csv';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { toast } from '@/components/ui/sonner';

const VIEW_VALUES = ['list', 'calendar'] as const;
const LAUNCH_RANGE_VALUES = [
  'today',
  'last_7_days',
  'last_30_days',
  'last_3_months',
  'since_beginning',
] as const;

const EMPTY_CAMPAIGNS: CampaignDto[] = [];

export default function CampaignsPage() {
  const navigate = useNavigate();
  const tenantSlug = useParams().tenantSlug ?? '';
  const { LL, locale } = useI18nContext();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const featureFlags = useFeatureFlags();

  const analyticsEnabled = featureFlags.data?.analyticsEnabled === true;
  const audiencesEnabled = featureFlags.data?.audiencesEnabled === true;

  const currency = currencyForDisplay(useStoreCurrency());

  const { localSettings, setLocalSettings } = useLocalSettings();
  const [queryView, setQueryView] = useQueryState(
    'view',
    parseAsStringLiteral(VIEW_VALUES).withDefault(localSettings.campaign.view)
  );

  const currentCalendarDate = sessionStorage.getItem('current-calendar-date');
  const currentView = queryView ?? localSettings.campaign.view;
  const [calendarDate, setCalendarDate] = useState<DateTime>(
    currentCalendarDate ? DateTime.fromISO(currentCalendarDate) : DateTime.now()
  );
  const [isExportVisibleDialogOpen, setIsExportVisibleDialogOpen] =
    useState(false);
  const [isExportingVisible, setIsExportingVisible] = useState(false);
  const [isExportingSelected, setIsExportingSelected] = useState(false);
  // Replaced by `status`; an old link's `showScheduled=false` reads as Enviadas.
  const [legacyShowScheduled, setLegacyShowScheduled] = useQueryState(
    'showScheduled',
    parseAsString
  );
  const [launchFromDate, setLaunchFromDate] = useQueryState(
    'launchFrom',
    parseAsString
  );
  const [launchToDate, setLaunchToDate] = useQueryState(
    'launchTo',
    parseAsString
  );
  const [predefinedLaunchRange, setPredefinedLaunchRangeRaw] = useQueryState(
    'launchRange',
    parseAsStringLiteral(LAUNCH_RANGE_VALUES)
  );
  const setPredefinedLaunchRange = useCallback(
    (value: string | undefined) => {
      if (value === undefined) {
        void setPredefinedLaunchRangeRaw(null);
        return;
      }
      if ((LAUNCH_RANGE_VALUES as readonly string[]).includes(value)) {
        void setPredefinedLaunchRangeRaw(
          value as (typeof LAUNCH_RANGE_VALUES)[number]
        );
      }
    },
    [setPredefinedLaunchRangeRaw]
  );

  const [page, setPage] = useState(1);

  // Status chips (both sizes) and, on mobile, the name search, in the URL.
  const [statusParam, setStatusParam] = useQueryState('status', parseAsString);
  const [searchParam, setSearchParam] = useQueryState(
    'q',
    parseAsString.withDefault('')
  );
  const [isSearchOpen, setIsSearchOpen] = useState(searchParam !== '');
  const statusFilter = parseStatusFilter(statusParam, legacyShowScheduled);
  const search = useDebouncedValue(searchParam.trim(), 300);
  const mobileCampaigns = useMobileCampaigns(statusFilter, search, isMobile);

  // The legacy param is read once, then `status` takes over in the URL.
  useEffect(() => {
    if (legacyShowScheduled === null) return;
    void setStatusParam(statusFilter === 'all' ? null : statusFilter);
    void setLegacyShowScheduled(null);
  }, [
    legacyShowScheduled,
    statusFilter,
    setStatusParam,
    setLegacyShowScheduled,
  ]);

  const handleStatusFilterChange = (filter: CampaignStatusFilter) => {
    setPage(1);
    void setStatusParam(filter === 'all' ? null : filter);
  };

  const toggleSearch = () => {
    if (isSearchOpen) void setSearchParam(null);
    setIsSearchOpen(!isSearchOpen);
  };

  const apiFilters = useMemo<CampaignsFilters>(() => {
    const filters: CampaignsFilters = {};
    if (launchFromDate) filters.launchDateMin = launchFromDate;
    if (launchToDate) filters.launchDateMax = launchToDate;
    const status = statusFilterToApi(statusFilter);
    if (status) filters.status = status;
    return filters;
  }, [launchFromDate, launchToDate, statusFilter]);

  const listOffset = (page - 1) * CAMPAIGNS_PAGE_SIZE;

  const listQuery = useCampaigns(
    apiFilters,
    CAMPAIGNS_PAGE_SIZE,
    listOffset,
    !isMobile
  );

  const campaigns = useMemo(
    () => listQuery.data?.data ?? EMPTY_CAMPAIGNS,
    [listQuery.data]
  );
  const total = listQuery.data?.total ?? 0;
  const isLoading = listQuery.isLoading;
  const error = listQuery.error;

  const calendarQuery = useInfiniteCampaigns(
    apiFilters,
    currentView === 'calendar' && !isMobile
  );

  const calendarCampaigns = useMemo(
    () => calendarQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [calendarQuery.data]
  );

  useEffect(() => {
    if (currentView !== 'calendar') return;
    if (!calendarQuery.hasNextPage || calendarQuery.isFetchingNextPage) return;
    calendarQuery.fetchNextPage();
  }, [
    currentView,
    calendarQuery.hasNextPage,
    calendarQuery.isFetchingNextPage,
    calendarQuery.data?.pages.length,
    calendarQuery.fetchNextPage,
  ]);

  useEffect(() => {
    setPage(1);
  }, [launchFromDate, launchToDate]);

  const launchDatePredefinedRanges = useMemo(
    () => [
      {
        label: LL.salesChart.predefinedRanges.today(),
        value: 'today',
        from: DateTime.now().startOf('day').toISO() ?? '',
        to: DateTime.now().endOf('day').toISO() ?? '',
      },
      {
        label: LL.salesChart.predefinedRanges.last7Days(),
        value: 'last_7_days',
        from: DateTime.now().minus({ days: 7 }).startOf('day').toISO() ?? '',
        to: DateTime.now().toISO() ?? '',
      },
      {
        label: LL.salesChart.predefinedRanges.last30Days(),
        value: 'last_30_days',
        from: DateTime.now().minus({ days: 30 }).startOf('day').toISO() ?? '',
        to: DateTime.now().toISO() ?? '',
      },
      {
        label: LL.salesChart.predefinedRanges.last3Months(),
        value: 'last_3_months',
        from: DateTime.now().minus({ months: 3 }).startOf('day').toISO() ?? '',
        to: DateTime.now().toISO() ?? '',
      },
      {
        label: LL.salesChart.predefinedRanges.sinceBeginning(),
        value: 'since_beginning',
        from: DateTime.fromMillis(0).toISO() ?? '',
        to: DateTime.now().toISO() ?? '',
      },
    ],
    [LL]
  );

  const hasDateSelection = launchFromDate !== null || launchToDate !== null;
  const isLaunchFilterActive = hasDateSelection;

  const handleSetCalendarDate = (date: DateTime) => {
    setCalendarDate(date);
    const iso = date.toISO();
    if (iso) sessionStorage.setItem('current-calendar-date', iso);
  };

  const changeCurrentView = (view: 'calendar' | 'list') => {
    void setQueryView(view);

    setLocalSettings({
      ...localSettings,
      campaign: {
        ...localSettings.campaign,
        view,
      },
    });
  };

  useEffect(() => {
    if (localSettings.campaign.view === currentView) {
      return;
    }

    setLocalSettings({
      ...localSettings,
      campaign: {
        ...localSettings.campaign,
        view: currentView,
      },
    });
  }, [currentView, localSettings, setLocalSettings]);

  const campaignsTableLabels = useMemo(
    () => ({
      title: LL.campaigns.table.title(),
      launchDate: LL.campaigns.table.launchDate(),
      audience: LL.campaigns.table.audience(),
      entireUserbase: LL.createCampaign.sections.audience.entireUserbase(),
      revenue: LL.campaigns.table.revenue(),
      actions: LL.campaigns.table.actions(),
      scheduled: LL.campaigns.scheduled(),
      draft: LL.campaigns.draft(),
      batch: LL.campaigns.importBatchLink(),
      paidOrderCount: LL.campaigns.table.paidOrderCount(),
      conversionRate: LL.campaigns.table.conversionRate(),
      impressions: LL.campaigns.table.impressions(),
      clicks: LL.campaigns.table.clicks(),
      ctr: LL.campaigns.table.ctr(),
      openCampaign: LL.campaigns.contextMenu.seeDetails(),
    }),
    [LL]
  );

  const campaignsDisplayLabels = useMemo(
    () => ({
      columns: LL.campaigns.table.columns(),
      noCampaignsFound: LL.campaigns.noCampaignsFound(),
    }),
    [LL]
  );

  const [selectionParam, setSelectionParam] = useQueryState(
    'selected',
    parseAsString
  );

  const rowSelection = useMemo<RowSelectionState>(() => {
    if (!selectionParam) return {};
    if (selectionParam === 'all') {
      const out: RowSelectionState = {};
      for (const campaign of campaigns) {
        out[campaign.id] = true;
      }
      return out;
    }
    const out: RowSelectionState = {};
    for (const id of selectionParam.split(',')) {
      if (id) out[id] = true;
    }
    return out;
  }, [selectionParam, campaigns]);

  useEffect(() => {
    if (!listQuery.data) return;
    if (!selectionParam || selectionParam === 'all') return;

    const visibleIds = new Set(campaigns.map((campaign) => campaign.id));
    const currentIds = selectionParam.split(',').filter(Boolean);
    const validIds = currentIds.filter((id) => visibleIds.has(id));

    if (validIds.length === currentIds.length) return;

    void setSelectionParam(validIds.length === 0 ? null : validIds.join(','));
  }, [campaigns, selectionParam, setSelectionParam]);

  const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      const selectedIds = Object.keys(next).filter((id) => next[id]);

      if (selectedIds.length === 0) {
        void setSelectionParam(null);
        return;
      }

      const visibleIds = campaigns.map((campaign) => campaign.id);
      const allVisibleSelected =
        visibleIds.length > 0 &&
        selectedIds.length === visibleIds.length &&
        visibleIds.every((id) => next[id]);

      if (allVisibleSelected) {
        if (selectionParam !== 'all') {
          void setSelectionParam('all');
        }
        return;
      }

      void setSelectionParam(selectedIds.join(','));
    },
    [rowSelection, campaigns, selectionParam, setSelectionParam]
  );

  const selectedCampaigns = useMemo(() => {
    const selectedIds = new Set(
      Object.keys(rowSelection).filter((id) => rowSelection[id])
    );
    if (selectedIds.size === 0) {
      return [];
    }

    return campaigns.filter((campaign) => selectedIds.has(campaign.id));
  }, [campaigns, rowSelection]);

  const selectedCount = selectedCampaigns.length;
  const canBulkDelete = canDeleteCampaigns(selectedCampaigns);

  const bulkDeleteMutation = useMutation({
    mutationFn: async (campaignIds: string[]) => {
      await Promise.all(
        campaignIds.map(async (campaignId) => {
          const result = await campaignsControllerCancelCampaign({
            path: { tenantSlug, campaignId },
          });
          const { status } = result.response;
          if (status >= 200 && status < 300) return;
          // Message by HTTP status (AD-025), never the body object itself.
          if (status === 409) {
            throw new Error(LL.campaigns.cancelOutcome.alreadySent());
          }
          if (status === 404) {
            throw new Error(LL.campaigns.cancelOutcome.notFound());
          }
          throw new Error(
            getApiErrorMessage(
              result.error,
              LL.campaigns.cancelOutcome.genericError()
            )
          );
        })
      );
    },
    onSuccess: async (_, campaignIds) => {
      await queryClient.invalidateQueries({
        queryKey: ['campaigns', tenantSlug],
      });
      void setSelectionParam(null);
      toast.success(LL.campaigns.bulkActions.deleteSuccessTitle(), {
        description: LL.campaigns.bulkActions.deleteSuccessDescription({
          count: campaignIds.length,
        }),
      });
    },
    onError: (err: unknown) => {
      toast.error(LL.campaigns.bulkActions.deleteErrorTitle(), {
        description: getApiErrorMessage(
          err,
          LL.campaigns.cancelOutcome.genericError()
        ),
      });
    },
  });

  const handleBulkDelete = useCallback(() => {
    if (!canBulkDelete || bulkDeleteMutation.isPending) {
      return;
    }

    bulkDeleteMutation.mutate(selectedCampaigns.map((campaign) => campaign.id));
  }, [bulkDeleteMutation, canBulkDelete, selectedCampaigns]);

  const csvLabels = useMemo(
    () => ({
      title: LL.campaigns.table.title(),
      launchDate: LL.campaigns.table.launchDate(),
      revenue: LL.campaigns.table.revenue(),
      paidOrderCount: LL.campaigns.table.paidOrderCount(),
      conversionRate: LL.campaigns.table.conversionRate(),
      clicks: LL.campaigns.table.clicks(),
      ctr: LL.campaigns.table.ctr(),
      notificationTitle:
        LL.campaigns.bulkActions.exportColumns.notificationTitle(),
      notificationBody:
        LL.campaigns.bulkActions.exportColumns.notificationBody(),
      notificationLink:
        LL.campaigns.bulkActions.exportColumns.notificationLink(),
      utmSource: LL.campaigns.bulkActions.exportColumns.utmSource(),
      utmMedium: LL.campaigns.bulkActions.exportColumns.utmMedium(),
      utmCampaign: LL.campaigns.bulkActions.exportColumns.utmCampaign(),
    }),
    [LL]
  );

  const exportFileName = useMemo(
    () => `campaigns_${DateTime.now().toFormat('yyyy-LL-dd_HH-mm-ss')}.csv`,
    []
  );

  const cancelCampaign = useCancelCampaign();
  const [cancelTarget, setCancelTarget] = useState<CampaignDto | null>(null);

  const { table, footerValues } = useCampaignsTable({
    campaigns: campaigns,
    analyticsEnabled,
    audiencesEnabled,
    tenantSlug,
    locale,
    currency,
    labels: campaignsTableLabels,
    onOpenCampaign: (campaignId) =>
      navigate(`/${tenantSlug}/campaigns/${campaignId}`),
    onDuplicateCampaign: (campaign) =>
      navigate(`/${tenantSlug}/campaigns/create?from=${campaign.id}`),
    onCancelCampaign: setCancelTarget,
    rowSelection,
    onRowSelectionChange: handleRowSelectionChange,
  });

  const handleExportVisible = useCallback(() => {
    try {
      setIsExportingVisible(true);
      exportCampaignsCsv({
        campaigns: table.getRowModel().rows.map((row) => row.original),
        analyticsEnabled,
        labels: csvLabels,
        fileName: exportFileName,
      });
      toast.success(LL.campaigns.bulkActions.exportSuccessTitle());
      setIsExportVisibleDialogOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(LL.campaigns.bulkActions.exportErrorTitle(), {
        description: message,
      });
    } finally {
      setIsExportingVisible(false);
    }
  }, [LL, analyticsEnabled, csvLabels, exportFileName, table]);

  const handleExportSelected = useCallback(() => {
    if (selectedCampaigns.length === 0) {
      return;
    }

    try {
      setIsExportingSelected(true);
      exportCampaignsCsv({
        campaigns: selectedCampaigns,
        analyticsEnabled,
        labels: csvLabels,
        fileName: exportFileName,
      });
      toast.success(LL.campaigns.bulkActions.exportSuccessTitle());
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(LL.campaigns.bulkActions.exportErrorTitle(), {
        description: message,
      });
    } finally {
      setIsExportingSelected(false);
    }
  }, [LL, analyticsEnabled, csvLabels, exportFileName, selectedCampaigns]);

  const handleExportLoaded = () => {
    try {
      exportCampaignsCsv({
        campaigns: mobileCampaigns.campaigns,
        analyticsEnabled,
        labels: csvLabels,
        fileName: exportFileName,
      });
      toast.success(LL.campaigns.bulkActions.exportSuccessTitle());
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(LL.campaigns.bulkActions.exportErrorTitle(), {
        description: message,
      });
    }
  };

  if (isMobile) {
    return (
      <SidebarLayout
        showNewCampaignFab
        mobileHeader={{
          title: LL.campaigns.title(),
          actions: (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={LL.campaigns.mobileList.search()}
                aria-expanded={isSearchOpen}
                className={mobileLayer.touch}
                onClick={toggleSearch}
              >
                <Search className="size-5" aria-hidden />
              </Button>
              <CampaignsMobileHeaderMenu
                loadedCount={mobileCampaigns.campaigns.length}
                onImport={() => navigate(`/${tenantSlug}/campaigns/import`)}
                onExport={handleExportLoaded}
              />
            </>
          ),
        }}
      >
        {isSearchOpen && (
          <Input
            type="search"
            autoFocus
            value={searchParam}
            maxLength={100}
            onChange={(event) =>
              void setSearchParam(event.target.value || null)
            }
            placeholder={LL.campaigns.mobileList.searchPlaceholder()}
            aria-label={LL.campaigns.mobileList.search()}
            className={cn('mb-4 shrink-0', mobileLayer.input)}
          />
        )}
        <CampaignsMobileList
          filter={statusFilter}
          search={search}
          onFilterChange={handleStatusFilterChange}
          currency={currency}
          analyticsEnabled={analyticsEnabled}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout showNewCampaignFab>
      <CancelCampaignDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !cancelCampaign.isPending) setCancelTarget(null);
        }}
        campaignName={cancelTarget?.name ?? ''}
        isPending={cancelCampaign.isPending}
        onConfirm={() => {
          if (!cancelTarget) return;
          cancelCampaign.mutate(
            { campaignId: cancelTarget.id, campaignName: cancelTarget.name },
            { onSettled: () => setCancelTarget(null) }
          );
        }}
      />
      <CampaignsBulkActionsPopover
        selectedCount={selectedCount}
        canDelete={canBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
        isExporting={isExportingSelected}
        onDelete={handleBulkDelete}
        onExport={handleExportSelected}
        labels={{
          selected: (args) => LL.campaigns.bulkActions.selected(args),
          delete: LL.campaigns.bulkActions.delete(),
          deleting: LL.campaigns.bulkActions.deleting(),
          confirmTitle: LL.campaigns.bulkActions.confirmTitle(),
          confirmDescription: (args) =>
            LL.campaigns.bulkActions.confirmDescription(args),
          confirmAction: LL.campaigns.bulkActions.confirmAction(),
          cancel: LL.campaigns.goBackButton(),
          disabledTooltip: LL.campaigns.cannotCancelSentCampaign(),
          export: LL.campaigns.bulkActions.export(),
          exporting: LL.campaigns.bulkActions.exporting(),
        }}
      />
      <div className="space-y-6 mb-4 flex flex-col min-h-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {LL.campaigns.title()}
          </h1>
          <div className="flex flex-row flex-wrap gap-4 items-center">
            <CampaignStatusFilterControl
              value={statusFilter}
              onChange={handleStatusFilterChange}
            />
            {currentView === 'calendar' && (
              <>
                <p className="text-sm content-center">
                  {calendarDate
                    .setLocale(getLuxonLocale(locale))
                    .toLocaleString({
                      month: 'long',
                      year: 'numeric',
                    })}
                </p>

                <CalendarNavigation
                  currentDate={calendarDate}
                  setCurrentDate={handleSetCalendarDate}
                />
              </>
            )}
            {currentView === 'list' && (
              <div className="flex items-center gap-2">
                <AlertDialog
                  open={isExportVisibleDialogOpen}
                  onOpenChange={setIsExportVisibleDialogOpen}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setIsExportVisibleDialogOpen(true)}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    {LL.campaigns.bulkActions.export()}
                  </Button>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {LL.campaigns.bulkActions.exportConfirmTitle()}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {LL.campaigns.bulkActions.exportConfirmDescription()}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {LL.campaigns.goBackButton()}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleExportVisible}
                        disabled={isExportingVisible}
                      >
                        {isExportingVisible
                          ? LL.campaigns.bulkActions.exporting()
                          : LL.campaigns.bulkActions.export()}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="relative shrink-0"
                    >
                      <Funnel className="h-4 w-4" aria-hidden />
                      {LL.campaigns.filters.title()}
                      {isLaunchFilterActive && (
                        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-blue-500" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <p className="text-sm font-medium">
                          {LL.campaigns.filters.launchDate()}
                        </p>
                        <DateRangeFilter
                          fromDate={launchFromDate}
                          setFromDate={setLaunchFromDate}
                          toDate={launchToDate}
                          setToDate={setLaunchToDate}
                          predefinedDateRange={
                            predefinedLaunchRange ?? undefined
                          }
                          setPredefinedDateRange={setPredefinedLaunchRange}
                          predefinedDateRanges={launchDatePredefinedRanges}
                          defaultDateRange="last_30_days"
                          allowOpenEnded
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <CampaignsTableColumnVisibilityMenu
                  table={table}
                  analyticsEnabled={analyticsEnabled}
                  labels={{ columns: campaignsDisplayLabels.columns }}
                />
              </div>
            )}
            <div className="">
              <Button
                size={'sm'}
                className="rounded-br-none rounded-tr-none border border-border"
                variant={currentView === 'list' ? 'secondary' : 'ghost'}
                onClick={() => changeCurrentView('list')}
              >
                <List className="mr-2 h-4 w-4" /> {LL.campaigns.views.list()}
              </Button>
              <Button
                size={'sm'}
                className="rounded-bl-none rounded-tl-none border border-border"
                variant={currentView === 'calendar' ? 'secondary' : 'ghost'}
                onClick={() => changeCurrentView('calendar')}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />{' '}
                {LL.campaigns.views.calendar()}
              </Button>
            </div>
            <Button
              size={'sm'}
              variant="outline"
              onClick={() => navigate(`/${tenantSlug}/campaigns/import`)}
            >
              <Upload className="mr-2 h-4 w-4" />{' '}
              {LL.campaigns.importCsvButton()}
            </Button>
            <Button
              size={'sm'}
              onClick={() => navigate(`/${tenantSlug}/campaigns/create`)}
            >
              <Plus className="mr-2 h-4 w-4" />{' '}
              {LL.campaigns.createCampaignButton()}
            </Button>
          </div>
        </div>
        <div
          className={cn('h-full', currentView === 'calendar' ? '' : 'hidden')}
        >
          <Calendar
            currentDate={calendarDate}
            setCurrentDate={handleSetCalendarDate}
            events={calendarCampaigns.map((campaign) => ({
              id: campaign.id,
              title: campaign.name,
              start: DateTime.fromISO(campaign.launchDateTime),
              status: campaign.status,
            }))}
          />
        </div>

        <div
          className={cn(
            'h-full max-h-full overflow-hidden',
            currentView === 'list' ? '' : 'hidden'
          )}
        >
          {total > 0 || isLoading ? (
            <CampaignsTable
              table={table}
              labels={campaignsDisplayLabels}
              footerValues={footerValues}
              page={page}
              pageSize={CAMPAIGNS_PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          ) : (
            <div className="text-center py-10">
              <p className="text-muted-foreground">
                {isLoading
                  ? LL.campaigns.loadingCampaigns()
                  : error
                    ? LL.campaigns.failedToLoadCampaigns()
                    : LL.campaigns.noCampaignsFound()}
              </p>
            </div>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
}
